import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import get from 'lodash/get';
import {
  isMessageApplicable,
} from '../../helpers/Location';
import LoopListItem from './LoopListItem';
import TagList from '../Tags/TagList';
import VibeCard from '../VibeCard/VibeCard';
import VibeCheckbox from '../VibeCheckbox/VibeCheckbox';
import VibeIcon from '../VibeIcon/VibeIcon';
import viTime from '../../icons/viTime';
import VibeTooltip from '../VibeTooltip/VibeTooltip';
import * as color from '../../sass/color.scss';
import './LoopCard.scss';

class LoopCard extends PureComponent {
  state = {
    hideNonApplicable: true,
  };

  /**
   * When the swap button is clicked
   */
  onClickSwapButton = () => {
    const {
      loop,
      onSwap,
    } = this.props;

    onSwap(loop);
  };

  /**
   * Toggle the "Hide Non Applicable Messages" filter
   */
  onToggleHideNonApplicable = ({ checked }) => {
    this.setState({
      hideNonApplicable: checked,
    });
  };

  toggleCard = () => {
    const {
      loop,
      toggleSelected,
      allowClick,
      disabled,
      selected,
      onClick,
    } = this.props;

    if (!disabled && allowClick) {
      // Send selected state back to the parent component
      toggleSelected(loop, !selected);
    } else {
      console.warn('Cannot select a disabled card');
    }

    onClick(loop);
  };

  render() {
    const {
      className,
      width,
      height,
      loop,
      companyId,
      locationId,
      allowClick,
      hideTags,
      day,
      swap,
      swapDirection,
      withoutCardStyle,
      selected,
      disabled,
      link,
    } = this.props;

    const {
      hideNonApplicable,
    } = this.state;

    const {
      messages = [],
      locations: loopLocations = [],
      tags,
    } = loop;

    // a real location is selected when the consumer passes a non-wildcard locationId or companyId
    const hasSpecificLocation = (!!locationId && locationId !== '*')
      || (!!companyId && companyId !== '*');

    // determine applicability for each message and decide which messages should be shown
    const applicabilityFlags = messages.map(messageItem => isMessageApplicable({
      companyId,
      locationId,
      locations: messageItem.locations,
      parentLocations: loopLocations,
    }));

    const shouldFilter = hasSpecificLocation && hideNonApplicable;

    const visibleMessages = shouldFilter
      ? messages.filter((_, index) => applicabilityFlags[index])
      : messages;

    const hiddenCount = messages.length - visibleMessages.length;

    let totalTimeSeconds = 0;

    for (let i = 0; i < visibleMessages.length; i++) {
      totalTimeSeconds += get(visibleMessages[i], 'media[0].durationSeconds', 0);
    }

    const totalTime = moment.duration(totalTimeSeconds, 'seconds');
    const totalTimeString = moment().startOf('day').add(totalTime).format('m:ss');

    return (
      <VibeCard
        className={classNames('LoopCard', className, {
          'no-card-style': withoutCardStyle,
          disabled,
        })}
        width={width}
        height={height}
        style={{
          padding:
            withoutCardStyle
              ? 0
              : 16,
          marginRight:
            withoutCardStyle
              ? 0
              : 20,
          marginBottom:
            withoutCardStyle
              ? 0
              : 20,
        }}
        selected={selected}
        allowClick={allowClick}
        link={link}
        swap={swap}
        swapDirection={swapDirection}
        onClickSwapButton={this.onClickSwapButton}
        onClick={this.toggleCard}
        onSelectMenuItem={this.onSelectMenuItem}
      >
        <div className="card-header">
          <div className={classNames('title', { swap })}>
            <VibeTooltip
              title={loop.name}
              enterDelay={500}
            >
              <span>
                {loop.name}
              </span>
            </VibeTooltip>
          </div>
        </div>

        <div className="content">
          <div className="review-card">
            <VibeTooltip
              placement="top"
              title={shouldFilter
                ? 'Total duration of currently visible messages'
                : 'Total includes all messages active or otherwise'}
            >
              <div className="card-setting">
                <VibeIcon
                  className="icon"
                  icon={viTime}
                  color={color.manatee}
                  size={16}
                />
                <span className="setting-value">
                  {totalTimeString}
                </span>
              </div>
            </VibeTooltip>
          </div>

          {hasSpecificLocation && (
            <div className="filter-toolbar">
              <VibeCheckbox
                size={14}
                color={color.primary}
                label="Hide Non Applicable Messages"
                checked={hideNonApplicable}
                onChange={this.onToggleHideNonApplicable}
                rootStyle={{ padding: 0 }}
              />
            </div>
          )}

          <div className="list-container">
            {visibleMessages.map((messageItem, index) => {
              const originalIndex = messages.indexOf(messageItem);

              return (
                <LoopListItem
                  key={`${messageItem._id}-${originalIndex}`}
                  message={messageItem}
                  // Current day the message block is being shown on the schedule
                  day={day}
                  loopLocations={loop.locations}
                  locationId={locationId}
                  companyId={companyId}
                />
              );
            })}

            {shouldFilter && hiddenCount > 0 && (
              <div className="hidden-messages-separator">
                {hiddenCount} {hiddenCount === 1 ? 'message' : 'messages'} hidden
              </div>
            )}
          </div>

          {!hideTags && (
            <div className="tags-container">
              <TagList
                tags={tags}
              />
            </div>
          )}
        </div>
      </VibeCard>
    );
  }
}

LoopCard.propTypes = {
  className: PropTypes.string,
  /** Width of the card */
  width: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  /** Height of the card */
  height: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  /**
   * Object that describes the message that's being displayed
   */
  loop: PropTypes.object.isRequired,
  locationId: PropTypes.string,
  companyId: PropTypes.string,
  selected: PropTypes.bool,
  allowClick: PropTypes.bool,
  hideTags: PropTypes.bool,
  /** Day of the week the message block is being shown */
  day: PropTypes.string,
  /** Remove all card styling from the message card */
  withoutCardStyle: PropTypes.bool,
  /** Show swap icon */
  swap: PropTypes.bool,
  /** Swap direction */
  swapDirection: PropTypes.oneOf([
    'left',
    'right',
  ]),
  disabled: PropTypes.bool,
  toggleSelected: PropTypes.func,
  /** When a card is clicked (if allowed) */
  onClick: PropTypes.func,
  /** When the swap button is clicked */
  onSwap: PropTypes.func,
};

LoopCard.defaultProps = {
  className: '',
  locationId: '*',
  companyId: '*',
  width: 271,
  height: 400,
  selected: false,
  allowClick: false,
  hideTags: false,
  day: null,
  withoutCardStyle: false,
  swap: false,
  swapDirection: null,
  disabled: false,
  toggleSelected: () => {},
  onClick: () => {},
  onSwap: () => {},
};

export default LoopCard;
